import type {
  IAgoraRTC,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  IMicrophoneAudioTrack,
  ConnectionState,
} from 'agora-rtc-sdk-ng';
import {
  VOICE_CONNECTION_STATE,
  type VoiceCallHandlers,
  type VoiceCallService,
  type VoiceConnectionState,
  type VoiceJoinConfig,
} from '@hotel-voice/shared';
import { CLIENT_ERROR } from '@hotel-voice/shared';

/**
 * Agora Web SDK 4.x implementation of `VoiceCallService` for the guest browser.
 *
 * All Agora surface area for the guest app lives in this file. It receives a
 * channel, a UID and a short-lived token minted by Cloud Functions — it never
 * sees the App Certificate and never mints anything itself.
 */
/**
 * The SDK is ~1.5 MB and only matters once a guest actually calls, so it is
 * loaded on demand rather than in the initial bundle — the idle screen is the
 * one a guest opens on hotel Wi-Fi.
 */
let agoraModule: Promise<IAgoraRTC> | null = null;

function loadAgora(): Promise<IAgoraRTC> {
  agoraModule ??= import('agora-rtc-sdk-ng').then((module) => module.default);
  return agoraModule;
}

export class GuestVoiceService implements VoiceCallService {
  private rtc: IAgoraRTC | null = null;
  private client: IAgoraRTCClient | null = null;
  private microphone: IMicrophoneAudioTrack | null = null;
  private handlers: VoiceCallHandlers = {};
  private muted = false;
  private joined = false;

  async initialize(handlers: VoiceCallHandlers = {}): Promise<void> {
    this.handlers = handlers;

    if (this.client) return;

    this.rtc = await loadAgora();

    // Voice only: 'rtc' mode for a two-party conversation.
    this.client = this.rtc.createClient({ mode: 'rtc', codec: 'vp8' });

    // Agora handles reconnection itself; we only surface it to the UI so the
    // guest sees "Reconnecting…" instead of silence.
    this.client.on('connection-state-change', (state: ConnectionState) => {
      this.handlers.onConnectionStateChanged?.(mapConnectionState(state));
    });

    this.client.on('user-published', async (user, mediaType) => {
      if (mediaType !== 'audio' || !this.client) return;
      try {
        await this.client.subscribe(user, 'audio');
        // Autoplay the receptionist's voice the moment it is available — the
        // guest already granted a gesture by pressing Call Reception.
        user.audioTrack?.play();
        this.handlers.onRemoteJoined?.(toNumericUid(user));
      } catch (error) {
        this.handlers.onError?.(error);
      }
    });

    this.client.on('user-unpublished', (user, mediaType) => {
      if (mediaType === 'audio') this.handlers.onRemoteLeft?.(toNumericUid(user));
    });

    this.client.on('user-left', (user) => {
      this.handlers.onRemoteLeft?.(toNumericUid(user));
    });

    this.client.on('token-privilege-will-expire', () => {
      this.handlers.onTokenWillExpire?.();
    });

    this.client.on('exception', (event) => {
      // Quality events (high packet loss, etc.) — not fatal, but worth logging.
      console.warn('[agora] exception', event.code, event.msg);
    });
  }

  async join(config: VoiceJoinConfig): Promise<void> {
    if (!this.client) await this.initialize(this.handlers);
    if (!this.client) throw new Error('Agora client unavailable');

    // Create the microphone track first: a denied permission should fail before
    // we occupy a channel.
    this.microphone = await this.createMicrophoneTrack();

    await this.client.join(config.appId, config.channelName, config.token, config.uid);
    await this.client.publish([this.microphone]);
    this.joined = true;

    if (this.muted) {
      await this.microphone.setMuted(true);
    }
  }

  private async createMicrophoneTrack(): Promise<IMicrophoneAudioTrack> {
    const rtc = this.rtc ?? (await loadAgora());
    try {
      return await rtc.createMicrophoneAudioTrack({
        AEC: true,
        ANS: true,
        AGC: true,
        encoderConfig: 'speech_standard',
      });
    } catch (error) {
      // Normalize the browser's permission errors into our own vocabulary so
      // the UI can show one clear sentence.
      const name = (error as { name?: string; code?: string }).name;
      const code = (error as { code?: string }).code;

      if (name === 'NotAllowedError' || code === 'PERMISSION_DENIED') {
        throw Object.assign(new Error('microphone permission denied'), {
          details: { code: CLIENT_ERROR.MIC_PERMISSION_DENIED },
        });
      }
      if (name === 'NotFoundError' || code === 'DEVICE_NOT_FOUND') {
        throw Object.assign(new Error('no microphone'), {
          details: { code: CLIENT_ERROR.MIC_NOT_FOUND },
        });
      }
      throw error;
    }
  }

  async leave(): Promise<void> {
    if (this.microphone) {
      this.microphone.stop();
      this.microphone.close();
      this.microphone = null;
    }

    if (this.client && this.joined) {
      await this.client.leave();
      this.joined = false;
    }
  }

  async mute(): Promise<void> {
    this.muted = true;
    await this.microphone?.setMuted(true);
  }

  async unmute(): Promise<void> {
    this.muted = false;
    await this.microphone?.setMuted(false);
  }

  isMuted(): boolean {
    return this.muted;
  }

  async renewToken(token: string): Promise<void> {
    await this.client?.renewToken(token);
  }

  async destroy(): Promise<void> {
    await this.leave();
    this.client?.removeAllListeners();
    this.client = null;
    this.rtc = null;
    this.handlers = {};
    this.muted = false;
  }
}

function mapConnectionState(state: ConnectionState): VoiceConnectionState {
  switch (state) {
    case 'CONNECTING':
      return VOICE_CONNECTION_STATE.CONNECTING;
    case 'CONNECTED':
      return VOICE_CONNECTION_STATE.CONNECTED;
    case 'RECONNECTING':
      return VOICE_CONNECTION_STATE.RECONNECTING;
    case 'DISCONNECTING':
    case 'DISCONNECTED':
      return VOICE_CONNECTION_STATE.DISCONNECTED;
    default:
      return VOICE_CONNECTION_STATE.FAILED;
  }
}

/** Agora allows string UIDs; ours are always numeric and server-allocated. */
function toNumericUid(user: IAgoraRTCRemoteUser): number {
  return typeof user.uid === 'number' ? user.uid : Number.parseInt(String(user.uid), 10);
}
